import { useEffect, useState } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, Users, Award, Shield, ArrowRight, Star, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState({ users: 0, hours: 0, exchanges: 0 });

  useEffect(() => {
    if (user) {
      if (user.premium_status) {
        navigate('/app');
      } else {
        navigate('/payment-required');
      }
    } else {
      // Fetch real stats for landing page
      fetchStats();
    }
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/stats/public`);
      setStats({
        users: response.data.total_users,
        hours: response.data.total_hours_exchanged,
        exchanges: response.data.total_exchanges
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Keep default values if fetch fails
      setStats({ users: 1250, hours: 5420, exchanges: 892 });
    }
  };

  const handleLogin = () => {
    const redirectUrl = `${BACKEND_URL}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-8 w-8 text-teal-500" />
              <span className="text-2xl font-bold text-gray-900">Chronos</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="min-h-[44px] min-w-[44px] flex items-center justify-center">
                <LanguageSelector />
              </div>
              <div className="min-h-[44px] min-w-[44px] flex items-center justify-center">
                <ThemeToggle />
              </div>
              <Button 
                data-testid="login-button"
                onClick={handleLogin}
                className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-full font-medium shadow-lg min-h-[44px]"
              >
                {t('landing.getStarted')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="fade-in-up">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              {t('landing.title')}
              <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent"> {t('landing.titleHighlight')}</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              {t('landing.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="hero-get-started-button"
                onClick={handleLogin}
                size="lg"
                className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-6 text-lg rounded-full font-semibold shadow-xl"
              >
                {t('landing.joinButton')} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* What is Chronos - Complete Explanation */}
          <div className="mt-16 max-w-6xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-12 border-2 border-teal-100 dark:border-teal-900">
              
              {/* Main Title */}
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Clock className="h-12 w-12 text-teal-500" />
                  <h2 className="text-4xl font-bold text-gray-900 dark:text-white">What is Chronos?</h2>
                </div>
                <p className="text-2xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
                  A <span className="font-bold text-teal-600 dark:text-teal-400">global community platform</span> where people exchange <span className="font-bold text-teal-600 dark:text-teal-400">time instead of money</span>
                </p>
              </div>

              {/* The Problem & Solution */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-red-50 dark:bg-red-900/10 rounded-2xl p-8 border-2 border-red-100 dark:border-red-900">
                  <h3 className="text-xl font-bold text-red-600 dark:text-red-400 mb-4">❌ The Problem</h3>
                  <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                    <li className="flex gap-2">
                      <span>•</span>
                      <span>Need help but can't afford expensive services</span>
                    </li>
                    <li className="flex gap-2">
                      <span>•</span>
                      <span>Have skills but no one to help</span>
                    </li>
                    <li className="flex gap-2">
                      <span>•</span>
                      <span>Language barriers prevent global collaboration</span>
                    </li>
                    <li className="flex gap-2">
                      <span>•</span>
                      <span>Hard to find trustworthy people</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 dark:bg-green-900/10 rounded-2xl p-8 border-2 border-green-100 dark:border-green-900">
                  <h3 className="text-xl font-bold text-green-600 dark:text-green-400 mb-4">✅ Chronos Solution</h3>
                  <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                    <li className="flex gap-2">
                      <span>•</span>
                      <span><strong>Time = Currency:</strong> 1 hour = 1 credit</span>
                    </li>
                    <li className="flex gap-2">
                      <span>•</span>
                      <span><strong>Fair Exchange:</strong> Everyone's time has equal value</span>
                    </li>
                    <li className="flex gap-2">
                      <span>•</span>
                      <span><strong>No Language Barriers:</strong> Auto-translate 140+ languages</span>
                    </li>
                    <li className="flex gap-2">
                      <span>•</span>
                      <span><strong>Trust System:</strong> Reviews & reputation scores</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* How It Works - Detailed */}
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-10 text-white mb-12">
                <h3 className="text-3xl font-bold mb-8 text-center">How It Works - Step by Step</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="bg-white/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-3xl font-bold">1</div>
                    <p className="font-bold mb-2">Sign Up FREE!</p>
                    <p className="text-sm text-teal-100">Join now during launch period - No payment required!</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-white/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-3xl font-bold">2</div>
                    <p className="font-bold mb-2">Offer Your Time</p>
                    <p className="text-sm text-teal-100">List your skills (Design, Coding, Cooking, etc.)</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-white/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-3xl font-bold">3</div>
                    <p className="font-bold mb-2">Help Others</p>
                    <p className="text-sm text-teal-100">1 hour of work = 1 credit earned</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-white/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-3xl font-bold">4</div>
                    <p className="font-bold mb-2">Request Help</p>
                    <p className="text-sm text-teal-100">Use your credits when you need something!</p>
                  </div>
                </div>
              </div>

              {/* Why Chronos - Key Benefits */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">Why Choose Chronos?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex gap-4 items-start">
                    <div className="bg-teal-100 dark:bg-teal-900 rounded-full p-3 flex-shrink-0">
                      <Users className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-1">Global Community</h4>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">Connect with people worldwide. Chat in ANY language - automatically translated!</p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="bg-teal-100 dark:bg-teal-900 rounded-full p-3 flex-shrink-0">
                      <Shield className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-1">Safe & Trustworthy</h4>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">Reputation system, reviews, and AI moderation keep the community safe.</p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="bg-purple-100 dark:bg-purple-900 rounded-full p-3 flex-shrink-0">
                      <Award className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-1">100% Revenue for Creators</h4>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">Teach courses, host workshops - keep 100% of earnings. Zero platform fees!</p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="bg-cyan-100 dark:bg-cyan-900 rounded-full p-3 flex-shrink-0">
                      <TrendingUp className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-1">Everyone's Time is Equal</h4>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">Whether you're a CEO or student - 1 hour is 1 hour. Fair for everyone.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Example Use Cases */}
              <div className="bg-gradient-to-br from-gray-50 to-teal-50 dark:from-gray-900 dark:to-teal-900/20 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-6">Real Examples</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow">
                    <p className="font-semibold text-teal-600 dark:text-teal-400 mb-2">🎨 Sarah (Designer)</p>
                    <p className="text-gray-600 dark:text-gray-300">Helps 3h with logo design → Earns 3 credits → Uses them for Italian lessons</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow">
                    <p className="font-semibold text-cyan-600 dark:text-cyan-400 mb-2">👨‍💻 Marco (Developer)</p>
                    <p className="text-gray-600 dark:text-gray-300">Teaches Python 2h → Earns 2 credits → Gets cooking lessons in return</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow">
                    <p className="font-semibold text-purple-600 dark:text-purple-400 mb-2">🎓 Lisa (Teacher)</p>
                    <p className="text-gray-600 dark:text-gray-300">Creates course → Students pay £20 → She keeps 100% (£20!)</p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="glass rounded-2xl p-6 shadow-lg fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="text-4xl font-bold text-teal-500 mb-2">{stats.users.toLocaleString()}</div>
              <div className="text-gray-600 font-medium">{t('landing.stats.members')}</div>
            </div>
            <div className="glass rounded-2xl p-6 shadow-lg fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="text-4xl font-bold text-cyan-500 mb-2">{stats.hours.toLocaleString()}</div>
              <div className="text-gray-600 font-medium">{t('landing.stats.hours')}</div>
            </div>
            <div className="glass rounded-2xl p-6 shadow-lg fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="text-4xl font-bold text-teal-500 mb-2">{stats.exchanges.toLocaleString()}</div>
              <div className="text-gray-600 font-medium">{t('landing.stats.exchanges')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('landing.howItWorks.title')}</h2>
            <p className="text-xl text-gray-600">{t('landing.howItWorks.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-6 rounded-2xl border-2 border-gray-100 hover:border-teal-500 hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('landing.howItWorks.signup.title')}</h3>
              <p className="text-gray-600">{t('landing.howItWorks.signup.desc')}</p>
            </div>

            <div className="p-6 rounded-2xl border-2 border-gray-100 hover:border-cyan-500 hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-cyan-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('landing.howItWorks.offer.title')}</h3>
              <p className="text-gray-600">{t('landing.howItWorks.offer.desc')}</p>
            </div>

            <div className="p-6 rounded-2xl border-2 border-gray-100 hover:border-teal-500 hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('landing.howItWorks.reputation.title')}</h3>
              <p className="text-gray-600">{t('landing.howItWorks.reputation.desc')}</p>
            </div>

            <div className="p-6 rounded-2xl border-2 border-gray-100 hover:border-cyan-500 hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mb-4">
                <Award className="h-6 w-6 text-cyan-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('landing.howItWorks.request.title')}</h3>
              <p className="text-gray-600">{t('landing.howItWorks.request.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-500 to-cyan-500 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <Shield className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-4">{t('landing.trust.title')}</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            {t('landing.trust.subtitle')}
          </p>
          <div className="flex items-center justify-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} className="h-8 w-8 fill-yellow-300 text-yellow-300" />
            ))}
            <span className="ml-4 text-2xl font-semibold">4.9/5 {t('landing.trust.rating')}</span>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            {t('landing.cta.title')}
          </h2>
          <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl p-8 text-white mb-8">
            <p className="text-5xl font-bold mb-2">FREE</p>
            <p className="text-xl font-semibold mb-2">First 6 Months Launch Special!</p>
            <p className="text-lg opacity-90">Join now - No payment required. Early adopters get lifetime access!</p>
          </div>
          <p className="text-xl text-gray-600 mb-8">
            {t('landing.cta.subtitle')}
          </p>
          <Button
            data-testid="footer-get-started-button"
            onClick={handleLogin}
            size="lg"
            className="bg-teal-500 hover:bg-teal-600 text-white px-12 py-6 text-lg rounded-full font-semibold shadow-xl"
          >
            Join FREE - Launch Special! <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            🎁 First 6 months: 100% FREE! Early adopters get lifetime access!
          </p>
          
          {/* Contact Support Box - Visible */}
          <div className="mt-8 bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 border-gray-200 dark:border-gray-700 max-w-md mx-auto">
            <h3 className="font-bold text-gray-900 dark:text-white mb-2">Need Help? 💬</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Questions about Chronos? Our support team is here to help!
            </p>
            <a 
              href="mailto:chronos.support@protonmail.com"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-full font-medium transition-all"
            >
              📧 Contact Support
            </a>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              chronos.support@protonmail.com
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-gray-950 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img src="/chronos-logo-white.png" alt="Chronos" className="h-8 w-8" />
            <span className="text-xl font-bold">Chronos</span>
          </div>
          <p className="text-gray-400 mb-2">© 2025 Chronos. {t('landing.footer')}</p>
          <p className="text-xs text-gray-500 mb-4">
            © 2025 Arcana Crypto Ltd. All rights reserved. Company No. 16371124 — Registered in England and Wales.
          </p>
          <div className="mb-4 p-4 bg-gray-800 rounded-lg">
            <p className="text-sm text-gray-300 mb-2">📧 Support & Questions</p>
            <a 
              href="mailto:chronos.support@protonmail.com" 
              className="text-teal-400 hover:text-teal-300 font-semibold text-lg hover:underline"
            >
              chronos.support@protonmail.com
            </a>
          </div>
          <div className="flex items-center justify-center gap-6 text-sm">
            <button
              onClick={() => navigate('/faq')}
              className="text-gray-400 hover:text-teal-400 transition-colors py-3 px-4 min-h-[44px]"
            >
              {t('faq.title')}
            </button>
            <span className="text-gray-600">•</span>
            <button
              onClick={() => navigate('/privacy')}
              className="text-gray-400 hover:text-teal-400 transition-colors py-3 px-4 min-h-[44px]"
            >
              {t('privacy.title')}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}